// src/pages/reports/CallcenterPerformancePage.jsx
import React, { useState, useMemo } from 'react';
import { RefreshCw, Users, TrendingUp, UserCog } from 'lucide-react';
import { getUserActivity, getUserSuccessRate } from '../../services/api/callcenterPerformance';
import { useAlert } from '../../contexts/AlertContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import CallcenterUserSelectionModal from './components/CallcenterUserSelectionModal';
import styles from './CallcenterPerformancePage.module.css';

// ─── Date Range Helpers ──────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const getDateRange = (key) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  switch (key) {
    case 'today': {
      const t = fmt(now);
      return { from: t, to: t };
    }
    case 'yesterday': {
      const y2 = new Date(now); y2.setDate(y2.getDate() - 1);
      const t = fmt(y2);
      return { from: t, to: t };
    }
    case 'this_week': {
      const day = now.getDay();
      const monday = new Date(now); monday.setDate(now.getDate() - ((day + 6) % 7));
      return { from: fmt(monday), to: fmt(now) };
    }
    case 'last_week': {
      const day = now.getDay();
      const lastMonday = new Date(now); lastMonday.setDate(now.getDate() - ((day + 6) % 7) - 7);
      const lastSunday = new Date(lastMonday); lastSunday.setDate(lastMonday.getDate() + 6);
      return { from: fmt(lastMonday), to: fmt(lastSunday) };
    }
    case 'this_month': {
      const from = new Date(y, m, 1);
      const to = new Date(y, m + 1, 0);
      return { from: fmt(from), to: fmt(to) };
    }
    case 'last_month': {
      const from = new Date(y, m - 1, 1);
      const to = new Date(y, m, 0);
      return { from: fmt(from), to: fmt(to) };
    }
    case 'month_minus_2': {
      const from = new Date(y, m - 2, 1);
      const to = new Date(y, m - 1, 0);
      return { from: fmt(from), to: fmt(to) };
    }
    case 'month_minus_3': {
      const from = new Date(y, m - 3, 1);
      const to = new Date(y, m - 2, 0);
      return { from: fmt(from), to: fmt(to) };
    }
    case 'month_minus_4': {
      const from = new Date(y, m - 4, 1);
      const to = new Date(y, m - 3, 0);
      return { from: fmt(from), to: fmt(to) };
    }
    default:
      return null;
  }
};

const buildRangeOptions = () => {
  const now = new Date();
  const m = now.getMonth();
  return [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'this_week', label: 'This Week' },
    { key: 'last_week', label: 'Last Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'month_minus_2', label: MONTH_NAMES[(m - 2 + 12) % 12] },
    { key: 'month_minus_3', label: MONTH_NAMES[(m - 3 + 12) % 12] },
    { key: 'month_minus_4', label: MONTH_NAMES[(m - 4 + 12) % 12] },
    { key: 'custom', label: 'Custom' },
  ];
};

// ─── Main Component ──────────────────────────────────────────────────────────

const CallcenterPerformancePage = () => {
  const { error: showError, success } = useAlert();
  const { t } = useLocalization();
  const [showUsersModal, setShowUsersModal] = useState(false);

  const [activeView, setActiveView] = useState('activity'); // 'activity' | 'success'
  const [selectedRange, setSelectedRange] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [successView, setSuccessView] = useState('confirmed'); // 'confirmed' | 'created'
  const [dateField, setDateField] = useState('createdAt');

  const rangeOptions = useMemo(() => buildRangeOptions(), []);

  const getEffectiveRange = () => {
    if (selectedRange === 'custom') return { from: customFrom, to: customTo };
    return getDateRange(selectedRange);
  };

  const handleGenerate = async () => {
    const range = getEffectiveRange();
    if (!range?.from || !range?.to) {
      showError('Please select a valid date range.');
      return;
    }
    setLoading(true);
    try {
      if (activeView === 'activity') {
        const res = await getUserActivity({ dateFrom: range.from, dateTo: range.to });
        setActivityData(res.data);
      } else {
        const res = await getUserSuccessRate({ dateFrom: range.from, dateTo: range.to, dateField });
        setSuccessData(res.data);
      }
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to fetch data.');
    } finally {
      setLoading(false);
    }
  };

  const renderActivityTable = () => {
    if (!activityData) return <EmptyPrompt />;
    if (!activityData.length) return <EmptyState message="No activity data found for the selected period." />;

    const totals = activityData.reduce((acc, row) => {
      acc.created += row.created;
      acc.edited += row.edited;
      acc.confirmed += row.confirmed;
      acc.notConfirmed += row.notConfirmed;
      acc.hold += row.hold;
      acc.cancelled += row.cancelled;
      return acc;
    }, { created: 0, edited: 0, confirmed: 0, notConfirmed: 0, hold: 0, cancelled: 0 });

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th className={styles.numCol}>Created</th>
              <th className={styles.numCol}>Edited</th>
              <th className={styles.numCol}>Confirmed</th>
              <th className={styles.numCol}>Not Confirmed</th>
              <th className={styles.numCol}>Hold</th>
              <th className={styles.numCol}>Cancelled</th>
            </tr>
          </thead>
          <tbody>
            {activityData.map((row) => (
              <tr key={row.name}>
                <td className={styles.nameCell}>{row.name}</td>
                <td className={styles.numCol}>{row.created}</td>
                <td className={styles.numCol}>{row.edited}</td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgeGreen}`}>{row.confirmed}</span>
                </td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgeRed}`}>{row.notConfirmed}</span>
                </td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgeOrange}`}>{row.hold}</span>
                </td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgeGray}`}>{row.cancelled}</span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={styles.totalRow}>
              <td>Total</td>
              <td className={styles.numCol}>{totals.created}</td>
              <td className={styles.numCol}>{totals.edited}</td>
              <td className={styles.numCol}>{totals.confirmed}</td>
              <td className={styles.numCol}>{totals.notConfirmed}</td>
              <td className={styles.numCol}>{totals.hold}</td>
              <td className={styles.numCol}>{totals.cancelled}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderSuccessTable = () => {
    if (!successData) return <EmptyPrompt />;
    if (!successData.length) return <EmptyState message="No data found for the selected period." />;

    const col = successView; // 'confirmed' | 'created'

    const totals = successData.reduce((acc, row) => {
      acc.count += row[col].count;
      acc.delivered += row[col].delivered;
      acc.returned += row[col].returned;
      acc.withCourier += row[col].withCourier;
      return acc;
    }, { count: 0, delivered: 0, returned: 0, withCourier: 0 });

    const deliveryRate = (row) => {
      const total = row[col].count;
      if (!total) return '—';
      return `${((row[col].delivered / total) * 100).toFixed(1)}%`;
    };
    const totalDeliveryRate = totals.count ? `${((totals.delivered / totals.count) * 100).toFixed(1)}%` : '—';

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th className={styles.numCol}>Count</th>
              <th className={styles.numCol}>Delivered</th>
              <th className={styles.numCol}>Returned</th>
              <th className={styles.numCol}>With Courier</th>
              <th className={styles.numCol}>Delivery Rate</th>
            </tr>
          </thead>
          <tbody>
            {successData.map((row) => (
              <tr key={row.name}>
                <td className={styles.nameCell}>{row.name}</td>
                <td className={styles.numCol}>{row[col].count}</td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgeGreen}`}>{row[col].delivered}</span>
                </td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgeRed}`}>{row[col].returned}</span>
                </td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgeBlue}`}>{row[col].withCourier}</span>
                </td>
                <td className={styles.numCol}>
                  <span className={`${styles.badge} ${styles.badgePurple}`}>{deliveryRate(row)}</span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={styles.totalRow}>
              <td>Total</td>
              <td className={styles.numCol}>{totals.count}</td>
              <td className={styles.numCol}>{totals.delivered}</td>
              <td className={styles.numCol}>{totals.returned}</td>
              <td className={styles.numCol}>{totals.withCourier}</td>
              <td className={styles.numCol}>{totalDeliveryRate}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Callcenter Performance</h1>
          <p className={styles.pageSubtitle}>Monitor user activity and order success rates</p>
        </div>
        <button className={styles.btnSecondary} onClick={() => setShowUsersModal(true)}>
          <UserCog size={15} /> {t('report.ccPerformanceUsers.selectUsers')}
        </button>
      </div>

      {showUsersModal && (
        <CallcenterUserSelectionModal
          onClose={() => setShowUsersModal(false)}
          onSaved={() => { success(t('report.ccPerformanceUsers.saved')); setShowUsersModal(false); }}
          onError={showError}
        />
      )}

      {/* View Toggle */}
      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleBtn} ${activeView === 'activity' ? styles.toggleBtnActive : ''}`}
          onClick={() => setActiveView('activity')}
        >
          <Users size={15} /> User Activity
        </button>
        <button
          className={`${styles.toggleBtn} ${activeView === 'success' ? styles.toggleBtnActive : ''}`}
          onClick={() => setActiveView('success')}
        >
          <TrendingUp size={15} /> User Success Rate
        </button>
      </div>

      {/* Filters */}
      <div className={styles.filterCard}>
        {/* Date Range Pills */}
        <div className={styles.filterSection}>
          <p className={styles.filterLabel}>Date Range</p>
          <div className={styles.rangePills}>
            {rangeOptions.map(opt => (
              <button
                key={opt.key}
                className={`${styles.pill} ${selectedRange === opt.key ? styles.pillActive : ''}`}
                onClick={() => setSelectedRange(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Date */}
        {selectedRange === 'custom' && (
          <div className={styles.customDateRow}>
            <div className={styles.dateGroup}>
              <label className={styles.filterLabel}>From</label>
              <input type="date" className={styles.dateInput} value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
            </div>
            <div className={styles.dateGroup}>
              <label className={styles.filterLabel}>To</label>
              <input type="date" className={styles.dateInput} value={customTo} onChange={e => setCustomTo(e.target.value)} />
            </div>
          </div>
        )}

        {/* Success Rate specific filters */}
        {activeView === 'success' && (
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <p className={styles.filterLabel}>Filter Orders By</p>
              <div className={styles.segmented}>
                <button
                  className={`${styles.segBtn} ${dateField === 'createdAt' ? styles.segBtnActive : ''}`}
                  onClick={() => setDateField('createdAt')}
                >
                  Created Date
                </button>
                <button
                  className={`${styles.segBtn} ${dateField === 'dispatchedDate' ? styles.segBtnActive : ''}`}
                  onClick={() => setDateField('dispatchedDate')}
                >
                  Dispatched Date
                </button>
              </div>
            </div>
          </div>
        )}

        <button className={styles.btnPrimary} onClick={handleGenerate} disabled={loading}>
          {loading ? <><span className={styles.spinner} /> Generating...</> : <><RefreshCw size={15} /> Generate</>}
        </button>
      </div>

      {/* Success Rate Sub-toggle */}
      {activeView === 'success' && successData && (
        <div className={styles.subToggle}>
          <button
            className={`${styles.subToggleBtn} ${successView === 'confirmed' ? styles.subToggleBtnActive : ''}`}
            onClick={() => setSuccessView('confirmed')}
          >
            Confirmed Orders
          </button>
          <button
            className={`${styles.subToggleBtn} ${successView === 'created' ? styles.subToggleBtnActive : ''}`}
            onClick={() => setSuccessView('created')}
          >
            Created Orders
          </button>
        </div>
      )}

      {/* Table Card */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loadingState}>
            <span className={styles.spinnerDark} />
            <span>Loading...</span>
          </div>
        ) : (
          activeView === 'activity' ? renderActivityTable() : renderSuccessTable()
        )}
      </div>
    </div>
  );
};

const EmptyPrompt = () => (
  <div className={styles.emptyState}>
    <p>Select a date range and click Generate to view data.</p>
  </div>
);

const EmptyState = ({ message }) => (
  <div className={styles.emptyState}>
    <p>{message}</p>
  </div>
);

export default CallcenterPerformancePage;